import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Candy, Store } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { halloweenAPI, useHalloween, setCandy } from './halloween';

// Placeholder jack-o'-lantern until the real artwork is provided
export const JackOLantern = ({ className = '' }) => (
  <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
    <path d="M32 14c-2-6 2-10 6-10" stroke="#3f6b2a" strokeWidth="4" fill="none" strokeLinecap="round" />
    <ellipse cx="20" cy="38" rx="14" ry="20" fill="#e8650f" />
    <ellipse cx="44" cy="38" rx="14" ry="20" fill="#e8650f" />
    <ellipse cx="32" cy="38" rx="14" ry="21" fill="#f7801d" />
    <path d="M18 32l6-7 6 7zM34 32l6-7 6 7z" fill="#2a1200" />
    <path d="M16 42q16 14 32 0l-5 2-3 4-4-4-4 4-4-4-4 4-3-4z" fill="#2a1200" />
  </svg>
);

const KIND_LABEL = { theme: 'Site theme', item: 'Video player skin', frame: 'Avatar aura' };
const OWNED_NOTE = { theme: 'pick it in Profile', item: 'toggle in Inventory', frame: 'equip in Inventory' };
const BUY_NOTE = { theme: 'Choose it any time in Profile → Themes.', item: 'Turn it on or off in your Inventory → Items.', frame: 'Equip it in your Inventory → Frames.' };
const API_URL = process.env.REACT_APP_BACKEND_URL;
const assetUrl = (u) => (u?.startsWith('/api/') ? `${API_URL}${u}` : u);

const ShopItem = ({ item, candy, onBuy, buying }) => {
  const img = assetUrl(item.image_url || item.item_icon);
  const canAfford = candy >= item.price;
  return (
    <div className="rounded-lg border border-purple-500/25 bg-white/5 overflow-hidden flex flex-col" data-testid={`halloween-item-${item.id}`}>
      <div className={`h-20 ${item.category === 'theme' ? 'bg-cover bg-center' : 'flex items-center justify-center bg-purple-950/50'}`}
        style={item.category === 'theme' ? { backgroundImage: `url('${img}')` } : undefined}>
        {item.category !== 'theme' && <img src={img} alt={item.name} className={`h-16 w-auto object-contain ${item.category === 'frame' ? 'mix-blend-screen' : 'ghost-bob'}`} />}
      </div>
      <div className="p-2 flex-1 flex flex-col">
        <p className="text-xs font-semibold text-white leading-tight">{item.name}</p>
        <p className="text-[10px] text-purple-200/60 mt-0.5 flex-1">{KIND_LABEL[item.category]}</p>
        {item.purchased ? (
          <span className="mt-1.5 text-[11px] text-center text-green-300 py-1 rounded bg-green-500/10" data-testid={`halloween-owned-${item.id}`}>
            Owned · {OWNED_NOTE[item.category]}
          </span>
        ) : (
          <button onClick={() => onBuy(item)} disabled={!canAfford || buying === item.id} data-testid={`halloween-buy-${item.id}`}
            className="mt-1.5 text-[11px] font-semibold py-1 rounded bg-orange-500 hover:bg-orange-400 disabled:bg-gray-700 disabled:text-gray-400 text-white transition-colors flex items-center justify-center gap-1">
            <Candy className="w-3 h-3" /> {buying === item.id ? 'Buying...' : `${item.price} candy`}
          </button>
        )}
      </div>
    </div>
  );
};

const ShopDialog = ({ open, onOpenChange, candy }) => {
  const [items, setItems] = useState([]);
  const [buying, setBuying] = useState(null);
  const load = () => halloweenAPI.shop().then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);
  const buy = async (item) => {
    setBuying(item.id);
    try {
      const r = await halloweenAPI.purchase(item.id);
      setCandy(r.data.candy);
      toast.success(r.data.message, { description: BUY_NOTE[item.category] });
      (r.data.achievements || []).forEach((a) => toast.success(`Achievement unlocked: ${a}`, { description: 'New title added to your Achievements.' }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purchase failed');
    } finally {
      setBuying(null);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#120a1c] border border-orange-500/40 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="halloween-shop-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase italic tracking-wide text-2xl text-orange-400 flex items-center gap-2">
            <JackOLantern className="w-8 h-8" /> Halloween Shop
          </DialogTitle>
          <DialogDescription className="text-purple-200/70">Spend the candy you get from catching ghosts. Anything you buy is yours to keep and use all year.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between rounded-lg bg-orange-500/10 border border-orange-500/30 px-4 py-3">
          <span className="text-sm text-orange-200 flex items-center gap-2"><Candy className="w-4 h-4" /> Your candy</span>
          <span className="font-mono text-xl text-orange-300" data-testid="halloween-candy-balance">{candy}</span>
        </div>
        {items.length === 0 ? (
          <div className="text-center py-8 text-purple-200/70" data-testid="halloween-shop-empty">
            <Store className="w-10 h-10 mx-auto mb-3 text-purple-300/50" />
            <p className="font-medium text-white">The shop is still being stocked...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="halloween-shop-items">
            {items.map((i) => <ShopItem key={i.id} item={i} candy={candy} onBuy={buy} buying={buying} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const HalloweenShopButton = () => {
  const { active, candy } = useHalloween();
  const [open, setOpen] = useState(false);
  if (!active) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} title="Halloween Shop" aria-label="Open Halloween Shop"
        className="ml-1 p-1 rounded-lg hover:bg-orange-500/15 transition-transform hover:scale-110 flex-shrink-0" data-testid="halloween-shop-btn">
        <JackOLantern className="w-7 h-7 drop-shadow-[0_0_8px_rgba(255,120,0,0.7)]" />
      </button>
      <ShopDialog open={open} onOpenChange={setOpen} candy={candy} />
    </>
  );
};
